import React, { useState, useEffect, useCallback } from "react";
import userImg from "../assets/img/user.jpg";
import { domain } from "../auth/domain";
import {
  Box, Typography, Grid, Card, CardContent, TextField, Button,
  InputAdornment, IconButton, CircularProgress, Stack, Paper, Divider, Alert, AlertTitle, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SavingsIcon from "@mui/icons-material/Savings";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { useAuth } from "../contexts/AuthContext"; // Import useAuth
import LoadingSpinner from "../components/LoadingSpinner"; // Import a loading spinner
import swal from "sweetalert";
import { motion } from "framer-motion";
import {
  fetchMyWithdrawalRequest,
  fetchWithdrawalRequirements,
  submitWithdrawalRequest,
} from "../services/memberWithdrawalService";
import { useTranslation } from "react-i18next";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useNavigate } from "react-router-dom";
import { getActiveGroupFromUser, isNzengoGroup } from "../utils/groupCapabilities";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.4, ease: "easeOut" },
  }),
};

const UserProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth(); // Use the auth context
  const accessToken = localStorage.getItem("accessToken");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const currency = (n) => new Intl.NumberFormat("en-TZ").format(Number(n) || 0);
  const [withdrawalRequest, setWithdrawalRequest] = useState(null);
  const [withdrawalReason, setWithdrawalReason] = useState("");
  const [requirements, setRequirements] = useState(null);
  const [withdrawalLoading, setWithdrawalLoading] = useState(true);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [withdrawalError, setWithdrawalError] = useState(null);
  const [withdrawalDialogOpen, setWithdrawalDialogOpen] = useState(false);
  const activeGroup = getActiveGroupFromUser(user || {});
  const canRequestNzengoTransfer =
    isNzengoGroup(activeGroup) && String(activeGroup.status || "ACTIVE").toUpperCase() === "ACTIVE";

  const loadRequirements = useCallback(async () => {
    if (!user) return;
    try {
      const data = await fetchWithdrawalRequirements();
      setRequirements(data);
    } catch (error) {
      console.error("Failed to load withdrawal requirements", error);
    }
  }, [user]);

  const loadWithdrawalRequest = useCallback(async () => {
    if (!user) return;
    setWithdrawalLoading(true);
    try {
      const data = await fetchMyWithdrawalRequest();
      setWithdrawalRequest(data);
    } catch (error) {
      console.error("Failed to load withdrawal request", error);
      setWithdrawalRequest(null);
    } finally {
      setWithdrawalLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadRequirements();
      loadWithdrawalRequest();
    }
  }, [authLoading, user, loadRequirements, loadWithdrawalRequest]);

  const handleWithdrawalDialogClose = () => {
    if (submittingWithdrawal) return;
    setWithdrawalDialogOpen(false);
    setWithdrawalError(null);
    setWithdrawalReason("");
  };

  const handleWithdrawalSubmit = async (event) => {
    event.preventDefault();
    if (!withdrawalReason.trim()) {
      setWithdrawalError(t("Please provide a brief reason for leaving."));
      return;
    }
    try {
      setSubmittingWithdrawal(true);
      setWithdrawalError(null);
      const payload = { reason: withdrawalReason.trim() };
      const created = await submitWithdrawalRequest(payload);
      setWithdrawalRequest(created);
      setWithdrawalReason("");
      swal({
        title: t("Request submitted"),
        text: t("Group leadership will now review your withdrawal request."),
        icon: "success",
      });
      setWithdrawalDialogOpen(false);
      loadRequirements();
    } catch (error) {
      setWithdrawalError(error.message || t("Unable to submit your request right now."));
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const statusLabels = {
    PENDING_CHAIR_APPROVAL: t("Waiting for Chairperson"),
    PENDING_SECRETARY_APPROVAL: t("Waiting for Secretary"),
    PENDING_ACCOUNTANT_APPROVAL: t("Waiting for Accountant"),
    APPROVED: t("Completed"),
    REJECTED: t("Rejected"),
  };

  const statusSeverity = {
    PENDING_CHAIR_APPROVAL: "info",
    PENDING_SECRETARY_APPROVAL: "info",
    PENDING_ACCOUNTANT_APPROVAL: "warning",
    APPROVED: "success",
    REJECTED: "error",
  };

  const approvalSteps = [
    { key: "chair", label: t("Chairperson") },
    { key: "secretary", label: t("Secretary") },
    { key: "accountant", label: t("Accountant") },
  ];

  const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString();
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    let formErrors = {};
    let hasError = false;

    if (!oldPassword) {
      formErrors.oldPassword = t("Current password is required");
      hasError = true;
    }
    if (!newPassword) {
      formErrors.newPassword = t("New password is required");
      hasError = true;
    }
    if (newPassword !== confirmPassword) {
      formErrors.confirmPassword = t("Passwords do not match");
      hasError = true;
    }

    setErrors(formErrors);

    if (hasError) return;

    setShowSpinner(true);

    try {
      const response = await fetch(`${domain}auth/users/set_password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${accessToken}`,
        },
        body: JSON.stringify({
          current_password: oldPassword,
          new_password: newPassword,
        }),
      });

      if (response.ok) {
        swal({
          title: t("Password successfully updated"),
          icon: "success",
        });
        setOldPassword("");
        setNewPassword("");
      } else {
        const result = await response.json();
        let errorTitle = "Error";
        let errorMsgs = ["An error occurred"];

        if (result.new_password) {
          errorTitle = t("New Password Error");
          errorMsgs = result.new_password;
        } else if (result.current_password) {
          errorTitle = t("Current Password Error");
          errorMsgs = result.current_password;
        }

        swal({
          icon: "error",
          title: errorTitle,
          text: errorMsgs.join("\n"), // Join all error messages with a newline
        });
      }
    } catch (error) {
      swal({
        icon: "error",
        title: t("Error"),
        text: t("An error occurred"),
        timer: 2000,
      });
    } finally {
      setShowSpinner(false);
    }
  };

  if (authLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 3 }}>
          {t("My Savvy Community Profile")}
        </Typography>

        <Grid container spacing={3}>
          {/* Left Column: Profile Info */}
          <Grid item xs={12} md={5}>
            <motion.div variants={fadeIn} initial="hidden" animate="visible">
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Paper
                    elevation={4}
                    sx={{
                      width: 150, height: 150, borderRadius: '50%',
                      mx: 'auto', mb: 2, overflow: 'hidden'
                    }}
                  >
                    <img src={userImg} alt="user" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Paper>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{user?.full_name}</Typography>
                  <Typography variant="body2" color="text.secondary">{user?.group_name} - {user?.role}</Typography>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" />
                    <Typography variant="body1">{user?.user?.email || 'No email provided'}</Typography>
                  </Box> */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon color="action" />
                      <Typography variant="body1">{user?.phone_number}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>

            {/* Heir Information */}
            <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={0.5}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, mt: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <FamilyRestroomIcon sx={{ color: brand }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t("Next of Kin (Heir)")}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {user?.heir_id ? (
                      <>
                        <Typography><b>{t("Name")}:</b> {user.heir_full_name}</Typography>
                        <Typography><b>{t("Relationship")}:</b> {user.heir_relationship}</Typography>
                        <Typography><b>{t("Contact")}:</b> {user.heir_contact}</Typography>
                      </>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {t("Heir information not set. Please contact your group administrator.")}
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Right Column: Password Change Form */}
          <Grid item xs={12} md={7}>
            <motion.div variants={fadeIn} initial="hidden" animate="visible">
              <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {t("Membership Actions")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("Choose the membership-level action you want to take for your current group.")}
                      </Typography>
                    </Box>

                    {canRequestNzengoTransfer && (
                      <Paper
                        variant="outlined"
                        aria-label={t("Transfer to Another Nzengo action")}
                        sx={{ p: 2.5, borderRadius: 2 }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          alignItems={{ xs: "stretch", sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <SyncAltIcon sx={{ color: brand, mt: 0.3 }} />
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                {t("Transfer to Another Nzengo")}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t("Move to another Local Community Unit while preserving your membership history.")}
                              </Typography>
                            </Box>
                          </Stack>
                          <Button
                            variant="outlined"
                            onClick={() => navigate("/app/nzengo/transfer")}
                            sx={{
                              borderColor: brand,
                              color: brand,
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              "&:hover": { borderColor: brandHover, color: brandHover },
                            }}
                          >
                            {t("Request Transfer")}
                          </Button>
                        </Stack>
                      </Paper>
                    )}

                    <Paper
                      variant="outlined"
                      aria-label={t("Membership Withdrawal action")}
                      sx={{ p: 2.5, borderRadius: 2 }}
                    >
                      <Stack spacing={3}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                              {t("Membership Withdrawal")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("Requests flow through Chairperson → Secretary → Accountant. Financial clearance is verified automatically before payout.")}
                            </Typography>
                          </Box>
                          <Tooltip
                            title={
                              withdrawalRequest
                                ? t("You already have a withdrawal request in progress.")
                                : t("Start a new withdrawal request")
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => setWithdrawalDialogOpen(true)}
                                disabled={Boolean(withdrawalRequest)}
                                sx={{
                                  bgcolor: brand,
                                  color: "#fff",
                                  "&:hover": { bgcolor: brandHover },
                                  "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                                }}
                              >
                                <ExitToAppIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>

                        {requirements && (
                          <Alert severity={requirements.is_financially_clear ? "success" : "warning"}>
                            <AlertTitle>{t("Financial clearance")}</AlertTitle>
                            <Typography variant="body2">
                              {requirements.is_financially_clear
                                ? t("You currently have no outstanding obligations.")
                                : t("Please clear the following outstanding amounts before the request reaches the accountant:")}
                            </Typography>
                            {!requirements.is_financially_clear && (
                              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                                {Number(requirements.outstanding_loan_amount) > 0 && (
                                  <li>{t("Loans")}: {currency(requirements.outstanding_loan_amount)} TZS</li>
                                )}
                                {Number(requirements.unpaid_penalties) > 0 && (
                                  <li>{t("Penalties")}: {currency(requirements.unpaid_penalties)} TZS</li>
                                )}
                                {Number(requirements.pending_contributions) > 0 && (
                                  <li>{t("Pending contributions")}: {currency(requirements.pending_contributions)} TZS</li>
                                )}
                              </ul>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {t("Retention percentage")}: {requirements.retention_percent}% {t("of your current savings")} ({currency(requirements.current_balance || 0)} TZS) {t("remains with the group per policy")}.
                            </Typography>
                          </Alert>
                        )}

                    {withdrawalLoading ? (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : withdrawalRequest ? (
                      <Stack spacing={2}>
                        <Alert severity={statusSeverity[withdrawalRequest.status] || "info"}>
                          <AlertTitle>{statusLabels[withdrawalRequest.status] || t("Request details")}</AlertTitle>
                          {withdrawalRequest.status === "REJECTED"
                            ? t("Your request was rejected. Please talk to the leadership team for the next steps.")
                            : t("Your request is moving through the approval chain.")}
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t("Reason for withdrawal")}
                          </Typography>
                          <Typography>{withdrawalRequest.reason}</Typography>
                        </Paper>

                        {withdrawalRequest.rejection_reason && (
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {t("Rejection details")}
                            </Typography>
                            <Typography color="error">{withdrawalRequest.rejection_reason}</Typography>
                          </Paper>
                        )}

                        {(Number(withdrawalRequest.retention_amount) > 0 || Number(withdrawalRequest.refund_amount) > 0) && (
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {t("Retention breakdown")}
                            </Typography>
                            <Typography variant="body2">
                              {t("Amount retained by the group")} ({withdrawalRequest.retention_percent}%):{" "}
                              <strong>{currency(withdrawalRequest.retention_amount)} TZS</strong>
                            </Typography>
                            <Typography variant="body2">
                              {t("Refund issued to you")}:{" "}
                              <strong>{currency(withdrawalRequest.refund_amount)} TZS</strong>
                            </Typography>
                            {withdrawalRequest.finalized_at && (
                              <Typography variant="caption" color="text.secondary">
                                {t("Finalized on")} {formatDate(withdrawalRequest.finalized_at)}
                              </Typography>
                            )}
                          </Paper>
                        )}

                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                          {approvalSteps.map(({ key, label }) => {
                            const entry = withdrawalRequest?.approvals?.[key];
                            const approved = entry?.approved;
                            return (
                              <Paper key={key} variant="outlined" sx={{ flex: 1, p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  {label}
                                </Typography>
                                <Chip
                                  label={approved ? t("Approved") : t("Pending")}
                                  color={approved ? "success" : "default"}
                                  size="small"
                                  sx={{ mb: 0.5 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {approved && entry?.approved_by
                                    ? `${entry.approved_by} • ${formatDate(entry.approved_at)}`
                                    : t("Not approved yet")}
                                </Typography>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        {t("Use the arrow icon above to submit a withdrawal request once you are confident about the figures shown here.")}
                      </Alert>
                    )}
                      </Stack>
                    </Paper>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>

            {/* Financial Summary */}
            {/* <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Financial Snapshot
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <SavingsIcon color="primary" sx={{ mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">Main Balance</Typography>
                      <Typography fontWeight={700}>{currency(user?.balance || 0)} TZS</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <AccountBalanceWalletIcon color="success" sx={{ mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">Overpayment</Typography>
                      <Typography fontWeight={700}>{currency(user?.overpayment_balance || 0)} TZS</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <EventAvailableIcon sx={{ color: brand, mb: 1 }} />
                      <Typography variant="caption" color="text.secondary">Next Due Date</Typography>
                      <Typography fontWeight={700}>{user?.next_due_date || 'N/A'}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div> */}

            {/* Password Change */}
            <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {t("Security")}
                  </Typography>
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                      label={t("Current Password")}
                      type={showCurrentPassword ? "text" : "password"}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      error={!!errors.oldPassword}
                      helperText={errors.oldPassword}
                      required
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LockOutlinedIcon /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                              {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label={t("New Password")}
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      error={!!errors.newPassword}
                      helperText={errors.newPassword}
                      required
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LockOutlinedIcon /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label={t("Confirm New Password")}
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      required
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LockOutlinedIcon /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={showSpinner}
                      sx={{
                        mt: 3, py: 1.5, bgcolor: brand,
                        "&:hover": { bgcolor: brandHover },
                      }}
                    >
                      {showSpinner ? <CircularProgress size={24} color="inherit" /> : t("Update Password")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={withdrawalDialogOpen} onClose={handleWithdrawalDialogClose} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleWithdrawalSubmit}>
          <DialogTitle>{t("Submit Withdrawal Request")}</DialogTitle>
          <DialogContent dividers>
            {withdrawalError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {withdrawalError}
              </Alert>
            )}
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t("Share the reason you want to leave the group. Your explanation will be visible to the leadership team reviewing the request.")}
            </Typography>
            <TextField
              label={t("Reason for withdrawal")}
              placeholder={t("Explain briefly why you want to withdraw...")}
              multiline
              minRows={4}
              value={withdrawalReason}
              onChange={(e) => setWithdrawalReason(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              {t("Once the Chairperson and Secretary approve, the system re-checks loans, penalties, and contributions before the Accountant releases the final payout.")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleWithdrawalDialogClose} disabled={submittingWithdrawal}>
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submittingWithdrawal}
              sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            >
              {submittingWithdrawal ? <CircularProgress size={20} color="inherit" /> : t("Submit Request")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default UserProfile;
